// Offline Queue Service for Pulsate Global
// Handles message queuing when offline and syncing when back online
// Integrates with Service Worker for background sync

import serviceWorkerManager from './ServiceWorkerManager';

class OfflineQueueService {
  constructor() {
    this.queue = [];
    this.isOnline = navigator.onLine;
    this.listeners = new Set();
    this.syncInProgress = false;
    this.maxQueueSize = 100;
    this.storageKey = 'pulsate_offline_queue';
    this.useServiceWorker = false;
    
    // Load queue from storage on init
    this.loadQueue();
    
    // Setup network listeners
    this.setupNetworkListeners();
  }

  // Initialize the service
  async init() {
    console.log('[OfflineQueue] Service initialized, online:', this.isOnline);
    
    // Initialize Service Worker
    const swInitialized = await serviceWorkerManager.init();
    this.useServiceWorker = swInitialized && serviceWorkerManager.isSyncSupported;
    
    if (this.useServiceWorker) {
      console.log('[OfflineQueue] Background sync enabled via Service Worker');
      
      // Store token for SW access
      const token = localStorage.getItem('token');
      if (token) {
        await serviceWorkerManager.storeToken(token);
      }
      
      // Sync IndexedDB with localStorage queue
      await this.syncToIndexedDB();
      
      // Listen for sync complete events
      window.addEventListener('syncComplete', () => {
        this.loadQueue();
        this.notifyListeners();
      });
    } else {
      console.log('[OfflineQueue] Background sync not available, using fallback');
    }
    
    // Notify listeners after initialization to update UI with loaded queue
    setTimeout(() => this.notifyListeners(), 100);
    return this;
  }

  // Sync localStorage queue to IndexedDB for Service Worker access
  async syncToIndexedDB() {
    if (!this.useServiceWorker) return;
    
    for (const message of this.queue) {
      await serviceWorkerManager.queueMessageForSync(message);
    }
  }

  // Subscribe to queue changes
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  // Notify listeners
  notifyListeners() {
    const state = {
      isOnline: this.isOnline,
      queueLength: this.queue.length,
      queue: this.queue
    };
    this.listeners.forEach(cb => cb(state));
  }

  // Setup network change listeners
  setupNetworkListeners() {
    window.addEventListener('online', () => {
      console.log('[OfflineQueue] Back online, syncing queue...');
      this.isOnline = true;
      this.notifyListeners();
      this.syncQueue();
    });

    window.addEventListener('offline', () => {
      console.log('[OfflineQueue] Gone offline, queuing enabled');
      this.isOnline = false;
      this.notifyListeners();
    });

    // Listen for WebSocket reconnection events
    window.addEventListener('wsReconnect', () => {
      if (this.isOnline) {
        this.syncQueue();
      }
    });
  }

  // Load queue from localStorage
  loadQueue() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        this.queue = JSON.parse(stored);
        console.log('[OfflineQueue] Loaded', this.queue.length, 'queued items');
      }
    } catch (e) {
      console.error('[OfflineQueue] Error loading queue:', e);
      this.queue = [];
    }
  }

  // Save queue to localStorage
  saveQueue() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.queue));
    } catch (e) {
      console.error('[OfflineQueue] Error saving queue:', e);
    }
  }

  // Add message to queue
  async queueMessage(message) {
    if (this.queue.length >= this.maxQueueSize) {
      // Remove oldest message if queue is full
      this.queue.shift();
    }

    const queuedMessage = {
      ...message,
      id: message.id || `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      queued_at: new Date().toISOString(),
      status: 'queued',
      retries: 0
    };

    this.queue.push(queuedMessage);
    this.saveQueue();
    
    // Also queue in IndexedDB for Service Worker
    if (this.useServiceWorker) {
      await serviceWorkerManager.queueMessageForSync(queuedMessage);
    }
    
    this.notifyListeners();

    console.log('[OfflineQueue] Message queued:', queuedMessage.id);
    return queuedMessage;
  }

  // Update message status in queue
  async updateMessageStatus(messageId, status, serverMessageId = null) {
    const index = this.queue.findIndex(m => m.id === messageId);
    if (index !== -1) {
      if (status === 'sent') {
        // Remove from queue on success
        this.queue.splice(index, 1);
        // Also remove from IndexedDB
        if (this.useServiceWorker) {
          await serviceWorkerManager.removeFromQueue(messageId);
        }
      } else {
        this.queue[index].status = status;
        if (serverMessageId) {
          this.queue[index].server_id = serverMessageId;
        }
      }
      this.saveQueue();
      this.notifyListeners();
    }
  }

  // Get queued messages for a conversation
  getQueuedMessages(conversationId) {
    return this.queue.filter(m => m.conversation_id === conversationId);
  }

  // Get all queued messages
  getAllQueued() {
    return [...this.queue];
  }

  // Sync queue with server
  async syncQueue() {
    if (this.syncInProgress || this.queue.length === 0 || !this.isOnline) {
      return;
    }

    this.syncInProgress = true;
    console.log('[OfflineQueue] Starting sync of', this.queue.length, 'messages');

    const API = process.env.REACT_APP_BACKEND_URL + '/api';
    const token = localStorage.getItem('token');

    if (!token) {
      console.log('[OfflineQueue] No auth token, skipping sync');
      this.syncInProgress = false;
      return;
    }

    // Process queue items one by one
    for (const message of [...this.queue]) {
      if (message.status === 'sending') continue;
      
      try {
        message.status = 'sending';
        message.retries++;
        this.saveQueue();
        this.notifyListeners();

        const response = await fetch(`${API}/messages`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            conversation_id: message.conversation_id,
            content: message.content,
            message_type: message.message_type || 'text',
            media_url: message.media_url,
            doodle_data: message.doodle_data
          })
        });

        if (response.ok) {
          const serverMessage = await response.json();
          console.log('[OfflineQueue] Message synced:', message.id, '->', serverMessage.id);
          this.updateMessageStatus(message.id, 'sent', serverMessage.id);
          
          // Dispatch event for UI to update
          window.dispatchEvent(new CustomEvent('messageSynced', {
            detail: { tempId: message.id, serverMessage }
          }));
        } else {
          throw new Error(`Server returned ${response.status}`);
        }
      } catch (error) {
        console.error('[OfflineQueue] Failed to sync message:', message.id, error);
        
        if (message.retries >= 3) {
          message.status = 'failed';
        } else {
          message.status = 'queued';
        }
        this.saveQueue();
        this.notifyListeners();
      }
    }

    this.syncInProgress = false;
    console.log('[OfflineQueue] Sync complete, remaining:', this.queue.length);
    this.notifyListeners();
  }

  // Retry failed messages
  async retryFailed() {
    this.queue.forEach(m => {
      if (m.status === 'failed') {
        m.status = 'queued';
        m.retries = 0;
      }
    });
    this.saveQueue();
    
    // Re-queue in IndexedDB and trigger background sync
    if (this.useServiceWorker) {
      await this.syncToIndexedDB();
      await serviceWorkerManager.requestSync();
    }
    
    this.notifyListeners();
    this.syncQueue();
  }

  // Clear the queue
  async clearQueue() {
    this.queue = [];
    this.saveQueue();
    
    // Also clear IndexedDB
    if (this.useServiceWorker) {
      await serviceWorkerManager.clearQueue();
    }
    
    this.notifyListeners();
  }

  // Remove specific message from queue
  async removeFromQueue(messageId) {
    const index = this.queue.findIndex(m => m.id === messageId);
    if (index !== -1) {
      this.queue.splice(index, 1);
      this.saveQueue();
      
      // Also remove from IndexedDB
      if (this.useServiceWorker) {
        await serviceWorkerManager.removeFromQueue(messageId);
      }
      
      this.notifyListeners();
    }
  }

  // Get queue stats
  getStats() {
    return {
      total: this.queue.length,
      queued: this.queue.filter(m => m.status === 'queued').length,
      sending: this.queue.filter(m => m.status === 'sending').length,
      failed: this.queue.filter(m => m.status === 'failed').length,
      isOnline: this.isOnline,
      syncInProgress: this.syncInProgress,
      useServiceWorker: this.useServiceWorker
    };
  }
}

// Create singleton instance
const offlineQueueService = new OfflineQueueService();

export default offlineQueueService;
